import type { ComponentType } from 'react';
import { makeAuthProvider } from '@zg-rentals/auth-react';
import { isHotpads, isZillow } from '@zg-rentals/multibrand-support';
import type { Brand } from '@zg-rentals/multibrand-support';
import { zillowLoginRedirectHref } from '@zg-rentals/login-zillow/client';
import { logError } from '@zg-rentals/log-error';
import apolloClient from './lib/apollo';
import { AuthCheckWrapper_GetHotPadsUserDocument, AuthCheckWrapper_GetZillowUserDocument } from './types/GraphqlTypes';
import type { AuthCheckWrapper_GetHotPadsUserQuery, AuthCheckWrapper_GetZillowUserQuery } from './types/GraphqlTypes';
import Custom500 from './pages/500';

type RawUser =
  | NonNullable<AuthCheckWrapper_GetHotPadsUserQuery['RH_getHotpadsUser']>
  | NonNullable<AuthCheckWrapper_GetZillowUserQuery['RH_getZillowUser']>;

function formatRawUser(rawUser: RawUser) {
  return {
    ...rawUser,
    isSingleFamilyEnterpriseCustomer: rawUser.isSingleFamilyEnterpriseCustomer ?? undefined,
    summary: rawUser.summary
      ? {
          activeListingCount: rawUser.summary.activeListingCount ?? undefined,
          totalListingCount: rawUser.summary.totalListingCount ?? undefined,
        }
      : undefined,
    restrictions: rawUser.restrictions
      ? {
          ...rawUser.restrictions,
          ctaText: rawUser.restrictions.ctaText ?? '',
          ctaUrl: rawUser.restrictions.ctaUrl ?? '',
          isAllowed: !!rawUser.restrictions.isAllowed,
          messageCode: rawUser.restrictions.messageCode ?? '',
          title: rawUser.restrictions.title ?? '',
          partialMessages:
            rawUser.restrictions.partialMessages
              ?.filter((msg): msg is NonNullable<typeof msg> => msg !== null)
              .map((msg) => ({
                ...msg,
                isBold: msg.isBold ?? false,
                text: msg.text ?? '',
                url: msg.url ?? '',
                className: msg.className ?? '',
              })) ?? [],
        }
      : undefined,
  };
}

export type User = ReturnType<typeof formatRawUser>;

const Auth = makeAuthProvider<{ brand: Brand }, User>({
  fetchUser: async ({ brand }) => {
    if (isHotpads(brand)) {
      const result = await apolloClient.query<AuthCheckWrapper_GetHotPadsUserQuery>({
        query: AuthCheckWrapper_GetHotPadsUserDocument,
        fetchPolicy: 'cache-first',
      });
      return formatRawUser(result.data.RH_getHotpadsUser);
    }
    if (isZillow(brand)) {
      const result = await apolloClient.query<AuthCheckWrapper_GetZillowUserQuery>({
        query: AuthCheckWrapper_GetZillowUserDocument,
        fetchPolicy: 'cache-first',
      });
      return formatRawUser(result.data.RH_getZillowUser);
    }
    const error = new Error('Unsupported brand');
    logError({ error, context: { brand, info: 'Error fetching user - Unsupported brand' } });
    throw error;
  },

  authorizeUser: (user, { brand }) => {
    if (!user.loggedIn) return false;
    // HotPads has a "soft-login" state where the user is logged in but not authorized.
    // In this case userToken is absent, so we treat the user as unauthorized and let
    // AuthBoundary's onUnauthorized callback redirect them to /user/update-password.
    if (isHotpads(brand) && !user.userToken) return false;
    return true;
  },

  providerQueryOptions: {
    staleTime: 5 * 60 * 1000,
    retry: 1,
  },
});

function getBoundaryProps(brand: Brand) {
  return {
    loading: null,
    onError: <Custom500 brand={brand} />,
    redirectHref: isZillow(brand)
      ? zillowLoginRedirectHref({
          baseURL: '/',
          entryPoint: 'satellite-rachel-web',
        })
      : undefined,
    onUnauthorized: isHotpads(brand)
      ? (user: User) => {
          if (typeof window !== 'undefined') {
            if (!user.loggedIn) {
              const currentPath = window.location.pathname + window.location.search;
              window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
            } else if (!user.userToken && process.env.NODE_ENV !== 'development') {
              window.location.href = `${window.location.origin}/user/update-password?redirect=/renter-hub/inbox`;
            }
          }
          return null;
        }
      : undefined,
  };
}

export function withAuthBoundary<
  Props extends { user: Exclude<ReturnType<typeof Auth.useAuth>['user'], null>; brand: Brand },
>(Component: ComponentType<Props>, boundaryProps?: { loading?: React.ReactNode }) {
  type OuterProps = Omit<Props, 'user'>;

  const WithAuthBoundary = (props: OuterProps) => (
    <Auth.Boundary {...getBoundaryProps(props.brand)} {...boundaryProps}>
      {(user) => <Component {...(props as Props)} user={user} />}
    </Auth.Boundary>
  );

  WithAuthBoundary.displayName = `WithAuthBoundary(${Component.displayName || Component.name})`;

  return WithAuthBoundary;
}

export default Auth;

export const useAuth = Auth.useAuth;
